export type CarAssetEntry = {
  high?: string;
  mid?: string;
  low?: string;
  fallback?: string;
};

export type MapAssetEntry = {
  overlay?: string;
  colliders?: string;
  roadTexture?: string;
  terrainTexture?: string;
};

export type CarRaceAssetManifest = {
  version: number;
  cars?: Record<string, CarAssetEntry>;
  map?: MapAssetEntry;
  performance?: {
    highLodDistance?: number;
    midLodDistance?: number;
    lowLodDistance?: number;
  };
};

const PREFIX = process.env.PUBLIC_URL || '';
const manifestUrl = `${PREFIX}/assets/car-race/asset-manifest.json`;

const fallbackManifest: CarRaceAssetManifest = {
  version: 1,
  cars: {},
  map: {
    overlay: `${PREFIX}/assets/car-race/osm/track_overlay.png`,
    colliders: `${PREFIX}/assets/car-race/osm/colliders.json`,
    roadTexture: `${PREFIX}/assets/car-race/textures/road/asphalt_diffuse.jpg`,
    terrainTexture: `${PREFIX}/assets/car-race/textures/terrain/grass_diffuse.jpg`
  },
  performance: {
    highLodDistance: 0,
    midLodDistance: 35,
    lowLodDistance: 70
  }
};

let cachedManifest: CarRaceAssetManifest | null = null;
let loadingManifest: Promise<CarRaceAssetManifest> | null = null;

const withPrefix = (value?: string): string | undefined => {
  if (!value) return undefined;
  if (value.startsWith('http://') || value.startsWith('https://')) return value;
  if (value.startsWith('/')) return `${PREFIX}${value}`;
  return `${PREFIX}/${value}`;
};

export async function loadCarRaceAssetManifest(): Promise<CarRaceAssetManifest> {
  if (cachedManifest) return cachedManifest;
  if (loadingManifest) return loadingManifest;

  loadingManifest = fetch(manifestUrl)
    .then((response) => (response.ok ? response.json() : null))
    .then((payload: CarRaceAssetManifest | null) => {
      if (!payload || typeof payload !== 'object') return fallbackManifest;

      const merged: CarRaceAssetManifest = {
        ...fallbackManifest,
        ...payload,
        cars: {
          ...(fallbackManifest.cars || {}),
          ...(payload.cars || {})
        },
        map: {
          ...(fallbackManifest.map || {}),
          ...(payload.map || {})
        },
        performance: {
          ...(fallbackManifest.performance || {}),
          ...(payload.performance || {})
        }
      };

      cachedManifest = merged;
      return merged;
    })
    .catch(() => fallbackManifest)
    .finally(() => {
      loadingManifest = null;
    });

  return loadingManifest;
}

export function getDefaultCarAssetUrls(carId: string) {
  const basePath = `${PREFIX}/assets/car-race/cars/${carId}`;
  return {
    high: `${basePath}/high.glb`,
    mid: `${basePath}/mid.glb`,
    low: `${basePath}/low.glb`,
    fallback: `${basePath}/model.glb`
  };
}

export function normalizeCarAssetUrls(entry: CarAssetEntry | undefined, carId: string) {
  const defaults = getDefaultCarAssetUrls(carId);
  return {
    high: withPrefix(entry?.high) || defaults.high,
    mid: withPrefix(entry?.mid) || defaults.mid,
    low: withPrefix(entry?.low) || defaults.low,
    fallback: withPrefix(entry?.fallback) || defaults.fallback
  };
}

export function normalizeMapAssetUrls(entry: MapAssetEntry | undefined) {
  const map = entry || fallbackManifest.map || {};
  return {
    overlay: withPrefix(map.overlay),
    colliders: withPrefix(map.colliders),
    roadTexture: withPrefix(map.roadTexture),
    terrainTexture: withPrefix(map.terrainTexture)
  };
}
